<template>
    <div>
        <div
            class="flex relative"
            v-click-away="onClickAway"
            v-if="$feature.isEnabled('new-search-bar')"
        >
            <new-activix-input
                id="globalSearch"
                autocomplete="off"
                class="rounded-md flex-1"
                data-testid="main-search-input"
                input-class="h-full"
                :placeholder="$t('search.search')"
                ref="mainSearch"
                @click="onClickInput"
                @mouseenter="isHover = true"
                @mouseleave="isHover = false"
                @keyup.enter="triggerSearch(false)"
                @keyup.esc="isInputFocused = false"
                @input="onInputChange($event)"
                @keydown.tab="onSearchInputTab"
                v-model="searchValue"
            >
                <template #before>
                    <icon class="self-center ml-3 text-gray-700" name="regular/search" />
                </template>
                <template #after>
                    <transition
                        enter-active-class="transition duration-150"
                        enter-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="transition duration-150"
                        leave-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <button
                            class="flex items-center justify-center p-2 mr-2 hover:text-blue-600"
                            data-testid="main-search-clear"
                            tabindex="-1"
                            type="button"
                            @click="onClearSearch"
                            v-if="searchValue"
                        >
                            <icon name="bold/close" class="text-xs" />
                        </button>
                        <div
                            class="self-center pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4"
                            v-else-if="!isInputFocused && isHover"
                        >
                            <span
                                class="rounded bg-gray-150 px-1.5 py-0.5 text-xs text-gray-600 border border-gray-400"
                            >
                                /
                            </span>
                        </div>
                    </transition>
                </template>
            </new-activix-input>
            <search-box-results
                :account-id="contextAccount?.id"
                :results="quickSearchData"
                :search="searchValue"
                :search-types="searchTypes"
                :value="selectedSearchType"
                :user-id="authUser?.id"
                ref="searchBoxResults"
                @search="onClickSearchItem"
                @on-click-result="onClickResult"
                @input="onSelectedSearchType"
                v-if="isInputFocused"
            />
        </div>
        <div class="flex" v-else>
            <activix-input
                class="text-grey-700 flex-1 attached-r"
                input-class="h-full"
                :placeholder="$t('search.search')"
                :disabled="selectedSearchType == 'recent'"
                ref="mainSearch"
                @keyup.enter="triggerSearch(false)"
                v-model="searchValue"
            >
                <template #icon>
                    <icon class="text-grey-700" name="regular/search" />
                </template>
            </activix-input>
            <activix-tooltip
                placement="bottom"
                :content="$t('modal.newTab')"
                v-if="searchValue?.length || selectedSearchType == 'recent'"
            >
                <activix-button class="attached-l attached-r" @click="triggerSearch(true)">
                    <icon class="text-grey-600" name="regular/expand-6" />
                </activix-button>
            </activix-tooltip>
            <activix-select
                class="w-auto attached-l"
                trigger-padding-class-name="!text-ms py-2 px-3"
                autosize
                :options="searchTypes"
                v-model="selectedSearchType"
            />
        </div>
    </div>
</template>

<script lang="ts">
    import {
        ActivixInput as newActivixInput,
        ActivixSelect,
    } from '@/components/activix-ui/index.ts';
    import { useUserStore } from '@/store/modules/user/store.js';
    import { debounce } from 'lodash-es';
    import SearchBoxResults from '@/components/search/bar/SearchBoxResults.vue';
    import {
        SearchBar,
        type SearchBarItemObj,
        SearchBarItemType,
        SearchBarModelType,
    } from '@/entities/SearchBar.ts';
    import { defineComponent } from 'vue';
    import { useAccountStore } from '@/store/modules/account/store.js';
    import { useCustomerSearchAvailability } from '@/components/customerProfile/hooks/useCustomerSearchAvailability.ts';
    import { useLatestAxiosRequest } from '@/hooks/useLatestAxiosRequest.ts';
    import ActivixInput from './elements/ActivixInput.vue';

    interface State {
        searchValue: string | null | undefined;
        selectedSearchType: SearchBarModelType;
        isInputFocused: boolean;
        isHover: boolean;
        quickSearchData: any[];
    }

    export default defineComponent({
        name: 'MainSearch',

        emits: ['submit'],

        components: {
            ActivixSelect,
            SearchBoxResults,
            ActivixInput,
            newActivixInput,
        },

        setup() {
            const { canUseCustomerSearchInAccount } = useCustomerSearchAvailability();
            const quickSearchRequests = useLatestAxiosRequest();

            return {
                canUseCustomerSearchInAccount,
                quickSearchRequests,
            };
        },

        data(): State {
            return {
                searchValue: '',
                selectedSearchType: SearchBarModelType.LEAD,
                isInputFocused: false,
                isHover: false,
                quickSearchData: [],
            };
        },

        computed: {
            contextAccount() {
                return useAccountStore().contextAccount;
            },

            authUser(): any {
                return useUserStore().authUser;
            },

            searchTypes(): any {
                const options = ['lead', 'vehicle'];

                if (this.canUseCustomerSearchInAccount) {
                    options.push('customer');
                }

                if (this.authUser.isAdmin()) {
                    options.push('user');
                }

                options.push('recent');

                if (this.$feature.isEnabled('note_search_es')) {
                    options.push('note');
                }

                return options.map((option) => {
                    return {
                        name: option,
                        value: option,
                        label: this.$t(`search.${option}`),
                    };
                });
            },
        },

        watch: {
            '$route.query.value': {
                immediate: true,
                handler(newValue) {
                    if (this.$route.name !== 'search') {
                        return;
                    }

                    this.setValue(newValue);
                },
            },

            '$route.name': {
                immediate: true,
                handler(newValue, oldValue) {
                    if (
                        oldValue == 'search' &&
                        newValue != oldValue &&
                        this.selectedSearchType == SearchBarModelType.RECENT
                    ) {
                        this.selectedSearchType = SearchBarModelType.LEAD;
                    }
                },
            },

            '$route.query.type': {
                immediate: true,
                handler(newValue) {
                    if (this.$route.name !== 'search') {
                        return;
                    }

                    this.invalidateQuickSearch(true);
                    this.selectedSearchType = newValue;
                },
            },

            'contextAccount.id'(newValue, oldValue) {
                if (newValue === oldValue) {
                    return;
                }

                this.restartQuickSearch(this.searchValue);
            },

            canUseCustomerSearchInAccount(newValue, oldValue) {
                if (
                    newValue === oldValue ||
                    this.selectedSearchType !== SearchBarModelType.CUSTOMER
                ) {
                    return;
                }

                this.restartQuickSearch(this.searchValue);
            },

            selectedSearchType(newValue) {
                if (!this.$feature.isEnabled('new-search-bar')) {
                    if (newValue == 'recent') {
                        this.searchValue = '';
                    }

                    this.triggerSearch();
                }
            },
        },

        methods: {
            onClearSearch() {
                this.searchValue = '';
                this.invalidateQuickSearch(true);
                this.isInputFocused = true;
                (this.$refs?.mainSearch as any)?.focus();
            },

            onClickInput(): void {
                if (!this.isInputFocused && this.searchValue && !this.quickSearchData.length) {
                    this.isInputFocused = true;
                    this.triggerQuickSearch(this.searchValue);
                }

                this.isInputFocused = true;
            },

            onSearchInputTab(event: KeyboardEvent): void {
                if (event.shiftKey || !this.isInputFocused) {
                    return;
                }

                const didFocusFirstResult = (
                    this.$refs.searchBoxResults as any
                )?.focusFirstResult();

                if (didFocusFirstResult) {
                    event.preventDefault();
                }
            },

            onClickAway(): void {
                this.isInputFocused = false;
            },

            onSelectedSearchType(value: SearchBarModelType): void {
                this.invalidateQuickSearch(true);
                this.selectedSearchType = value;

                if (value === SearchBarModelType.RECENT) {
                    this.searchValue = '';
                } else {
                    (this.$refs?.mainSearch as any)?.focus();
                }

                this.triggerQuickSearch(this.searchValue);
            },

            setValue(value: any) {
                this.invalidateQuickSearch(true);
                this.searchValue = value || '';
            },

            triggerSearch(inNewTab = false): void {
                if (!this.searchValue?.trim() && this.selectedSearchType !== 'recent') {
                    return;
                }

                this.searchEnter(inNewTab);
            },

            onClickResult(route: object, openExternal: boolean = false): void {
                this.$behavior.track('SearchBarResultClick', {
                    type: this.selectedSearchType,
                    external: openExternal ? 'yes' : 'no',
                    fromRecent: this.searchValue ? 'no' : 'yes',
                });

                if (openExternal) {
                    window.open(this.$router.resolve(route).href, '_blank');
                } else {
                    this.$router.push(route);
                }

                this.isInputFocused = false;
            },

            onClickSearchItem(item: SearchBarItemObj, inNewTab: boolean) {
                this.searchValue = item.title;
                this.searchEnter(inNewTab);
            },

            searchEnter(inNewTab: boolean): void {
                const searchBarItem: SearchBarItemObj =
                    this.selectedSearchType === SearchBarModelType.CUSTOMER
                        ? {
                              accountId: Number(this.contextAccount?.id),
                              id: this.searchValue,
                              type: SearchBarModelType.CUSTOMER,
                              title: this.searchValue,
                              itemType: SearchBarItemType.SEARCH,
                              userId: Number(this.authUser?.id),
                          }
                        : {
                              id: this.searchValue,
                              type: this.selectedSearchType,
                              title: this.searchValue,
                              itemType: SearchBarItemType.SEARCH,
                          };

                SearchBar.set(searchBarItem);

                this.isInputFocused = false;

                const searchUrl = {
                    name: 'search',
                    query: {
                        type: this.selectedSearchType,
                        value: this.searchValue,
                    },
                };

                if (inNewTab) {
                    this.$behavior.track('Search', {
                        type: this.selectedSearchType,
                        external: 'yes',
                    });
                    window.open(this.$router.resolve(searchUrl).href, '_blank');
                } else {
                    this.$behavior.track('Search', {
                        type: this.selectedSearchType,
                        external: 'no',
                    });
                    this.$router.push(searchUrl);
                    this.$emit('submit');
                }
            },

            onInputChange(value: string | number): void {
                if (!this.isInputFocused) {
                    return;
                }

                const normalizedValue = String(value);
                this.invalidateQuickSearch(true);

                if (
                    this.selectedSearchType === SearchBarModelType.CUSTOMER &&
                    !this.canUseCustomerSearchInAccount
                ) {
                    return;
                }

                if (
                    normalizedValue.trim() &&
                    this.selectedSearchType === SearchBarModelType.RECENT
                ) {
                    this.selectedSearchType = SearchBarModelType.LEAD;
                }

                this.debounceQuickSearch(normalizedValue);
            },

            invalidateQuickSearch(cancelDebounce = false): void {
                if (cancelDebounce) {
                    (
                        this.debounceQuickSearch as typeof this.debounceQuickSearch & {
                            cancel?: () => void;
                        }
                    ).cancel?.();
                }

                this.quickSearchRequests.invalidate();
                this.quickSearchData = [];
            },

            restartQuickSearch(value?: string | null): void {
                this.invalidateQuickSearch(true);

                if (!this.isInputFocused) {
                    return;
                }

                this.debounceQuickSearch(value);
            },

            debounceQuickSearch(value?: string | null): void {
                this.triggerQuickSearch(value);
            },

            async triggerQuickSearch(value?: string | null): Promise<void> {
                if (!this.isInputFocused) {
                    return;
                }

                if (
                    this.selectedSearchType === SearchBarModelType.CUSTOMER &&
                    !this.canUseCustomerSearchInAccount
                ) {
                    return;
                }

                if (
                    this.selectedSearchType !== SearchBarModelType.RECENT &&
                    (!value || value.length < 2 || !value.trim())
                ) {
                    return;
                }

                const request = this.quickSearchRequests.begin();
                this.$wait.start('fetching.search-bar');
                try {
                    const { data } = await this.$axios.get('v1/search/raw', {
                        params: {
                            type: this.selectedSearchType,
                            term: value,
                            accountId: this.contextAccount?.id,
                            userId: this.authUser?.id,
                        },
                        signal: request.signal,
                    });

                    if (request.isCurrent()) {
                        this.quickSearchData = data.data ? data.data : data;
                    }
                } catch (error) {
                    if (request.isCurrent() && !this.quickSearchRequests.isCancellation(error)) {
                        this.quickSearchData = [];
                    }
                } finally {
                    this.$wait.end('fetching.search-bar');
                    request.release();
                }
            },

            handleHotkey(event: any): void {
                if (event.key !== '/' || !this.$refs.mainSearch || this.isInputFocused) {
                    return;
                }

                // If an input or textarea is focused, do not execute the global keydown logic
                const isInputOrTextarea = ['INPUT', 'TEXTAREA'].includes(
                    (document as any)?.activeElement?.tagName
                );

                if (isInputOrTextarea) {
                    return;
                }

                event.preventDefault();
                (this.$refs.mainSearch as any)?.focus();
                this.isInputFocused = true;
            },
        },

        mounted(): void {
            if (this.$feature.isEnabled('new-search-bar')) {
                globalThis.addEventListener('keydown', this.handleHotkey);
            }
        },

        beforeUnmount(): void {
            globalThis.removeEventListener('keydown', this.handleHotkey);
            this.invalidateQuickSearch(true);
        },

        created(): void {
            this.debounceQuickSearch = debounce(this.debounceQuickSearch, 250);
        },
    });
</script>
